import React, {
  createContext,
  useContext,
  useState,
} from "react";
import {
  FunctionField,
  List,
  NumberField,
  TextField as RaTextField,
  useNotify,
  useRecordContext,
  useRefresh,
  useSidebarState,
  useTranslate,
} from "react-admin";
import {
  Box,
  Button,
  Chip,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  MoreHoriz as MoreIcon,
  AddCircleOutline as TopUpIcon,
  TuneOutlined as AdjustIcon,
  LockOpen as LockOpenIcon,
  Lock as LockIcon,
} from "@mui/icons-material";
import {
  CustomDatagrid,
  FilterField,
  FILTER_HEIGHT,
  ListActionsBar,
} from "@/components/common";
import { extractErrorMessage } from "@/utils/extractErrorMessage";
import {
  blockBalance as blockBalanceApi,
  unblockBalance as unblockBalanceApi,
} from "../services/creditBalanceService";
import type { CreditBalanceRecord } from "../types/creditBalance";
import CreditPurchaseDialog, { type DialogMode } from "./CreditPurchaseDialog";

const i18nBase = "resources.credit-consumption/credit-balance";

interface RowActionContext {
  openTopUp: (record: CreditBalanceRecord) => void;
  openAdjust: (record: CreditBalanceRecord) => void;
  toggleBlocked: (record: CreditBalanceRecord) => Promise<void>;
}

const RowActionContext = createContext<RowActionContext | null>(null);

const useRowActions = (): RowActionContext => {
  const ctx = useContext(RowActionContext);
  if (!ctx) {
    throw new Error("Row action button used outside CreditBalanceList");
  }
  return ctx;
};

const RowMenu: React.FC = () => {
  const record = useRecordContext<CreditBalanceRecord>();
  const translate = useTranslate();
  const { openTopUp, openAdjust, toggleBlocked } = useRowActions();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  if (!record) return null;
  const isBlocked = record.blocked;
  const close = () => setAnchorEl(null);
  const item = (
    icon: React.ReactNode,
    labelKey: string,
    onClick: () => void,
    danger = false,
  ) => (
    <MenuItem
      onClick={(e) => {
        e.stopPropagation();
        close();
        onClick();
      }}
      sx={danger ? { color: "warning.main" } : undefined}
    >
      <ListItemIcon sx={{ color: "inherit" }}>{icon}</ListItemIcon>
      <ListItemText>{translate(labelKey)}</ListItemText>
    </MenuItem>
  );
  return (
    <>
      <IconButton
        size="small"
        aria-label="row actions"
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
      >
        <MoreIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={close} onClick={(e) => e.stopPropagation()}>
        {item(<TopUpIcon sx={{ fontSize: 18 }} />, `${i18nBase}.actions.topUp`, () => openTopUp(record))}
        {item(<AdjustIcon sx={{ fontSize: 18 }} />, `${i18nBase}.actions.adjust`, () => openAdjust(record))}
        {isBlocked
          ? item(<LockOpenIcon sx={{ fontSize: 18 }} />, `${i18nBase}.actions.unblock`, () => void toggleBlocked(record))
          : item(<LockIcon sx={{ fontSize: 18 }} />, `${i18nBase}.actions.block`, () => void toggleBlocked(record), true)}
      </Menu>
    </>
  );
};

const BlockedBadge: React.FC = () => {
  const record = useRecordContext<CreditBalanceRecord>();
  const translate = useTranslate();
  if (!record) return null;
  return record.blocked ? (
    <Chip
      label={translate(`${i18nBase}.status.blocked`)}
      color="error"
      size="small"
    />
  ) : (
    <Chip
      label={translate(`${i18nBase}.status.active`)}
      color="success"
      size="small"
    />
  );
};

interface ListActionsProps {
  onOpenPurchase: () => void;
}

const ListActions: React.FC<ListActionsProps> = ({ onOpenPurchase }) => {
  const translate = useTranslate();
  return (
    <ListActionsBar
      filters={
        <>
          <FilterField
            source="costCenterName"
            label={translate(`${i18nBase}.filters.costCenterName`)}
          />
        </>
      }
      addButton={
        <Button
          variant="contained"
          color="primary"
          onClick={onOpenPurchase}
          startIcon={<AddIcon />}
          aria-label={translate(`${i18nBase}.actions.creditPurchase`)}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            whiteSpace: "nowrap",
            flexShrink: 0,
            height: FILTER_HEIGHT,
            minWidth: { xs: FILTER_HEIGHT, sm: "auto" },
            borderRadius: 1.5,
            px: { xs: 0, sm: 2.5 },
            boxShadow: "none",
            "& .MuiButton-startIcon": {
              mr: { xs: 0, sm: 1 },
              ml: { xs: 0, sm: -0.5 },
            },
            "&:hover": { boxShadow: "0 2px 6px rgba(11, 165, 183, 0.25)" },
          }}
        >
          <Box
            component="span"
            sx={{ display: { xs: "none", sm: "inline" } }}
          >
            {translate(`${i18nBase}.actions.creditPurchase`)}
          </Box>
        </Button>
      }
    />
  );
};

export const CreditBalanceList: React.FC = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [sidebarOpen] = useSidebarState();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const maxWidth = isMobile ? "100%" : sidebarOpen ? "85vw" : "95vw";
  const [dialogMode, setDialogMode] = useState<DialogMode | null>(null);

  const handlers: RowActionContext = {
    openTopUp: (record) =>
      setDialogMode({
        kind: "topup",
        costCenterId: record.costCenterId,
        costCenterName: record.costCenterName,
      }),
    openAdjust: (record) =>
      setDialogMode({
        kind: "adjust",
        costCenterId: record.costCenterId,
        costCenterName: record.costCenterName,
      }),
    toggleBlocked: async (record) => {
      const action = record.blocked ? unblockBalanceApi : blockBalanceApi;
      const successKey = record.blocked
        ? `${i18nBase}.notifications.unblockSucceeded`
        : `${i18nBase}.notifications.blockSucceeded`;
      const failKey = record.blocked
        ? `${i18nBase}.notifications.unblockFailed`
        : `${i18nBase}.notifications.blockFailed`;
      try {
        await action(record.costCenterId);
        notify(successKey, { type: "success" });
        refresh();
      } catch (err: unknown) {
        notify(extractErrorMessage(err, failKey, translate), {
          type: "error",
        });
      }
    },
  };

  return (
    <RowActionContext.Provider value={handlers}>
      <Box>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: "text.primary",
            mb: 1,
            mt: 0,
            ml: 2,
          }}
        >
          {translate(`${i18nBase}.name`, { smart_count: 2 })}
        </Typography>
        <List
          actions={
            <ListActions
              onOpenPurchase={() => setDialogMode({ kind: "purchase" })}
            />
          }
          sort={{ field: "0", order: "ASC" }}
          storeKey={false}
          disableSyncWithLocation
          perPage={10}
          exporter={false}
          sx={{
            maxWidth,
            overflow: "visible",
            "& .RaList-main, & .RaList-content": {
              overflow: "visible",
              width: "100%",
              maxWidth: "100%",
            },
            "& .RaListToolbar-root, & .RaList-actions": {
              display: "flex",
              width: "100%",
              mb: { xs: 1, sm: 2 },
              px: 0,
              py: { xs: 0, sm: 1 },
              minHeight: "auto",
            },
          }}
        >
          <CustomDatagrid
            primarySource="costCenterName"
            bulkActionButtons={false}
            disableMobileView
          >
            <FunctionField
              label={translate(`${i18nBase}.columns.actions`)}
              render={() => <RowMenu />}
            />
            <RaTextField source="costCenterName" />
            <NumberField source="availableCredits" />
            <NumberField source="reservedCredits" />
            <FunctionField
              source="blocked"
              label={translate(`${i18nBase}.columns.status`)}
              render={() => <BlockedBadge />}
            />
          </CustomDatagrid>
        </List>
        <CreditPurchaseDialog
          mode={dialogMode}
          onClose={() => setDialogMode(null)}
          onSuccess={() => {
            setDialogMode(null);
            refresh();
          }}
        />
      </Box>
    </RowActionContext.Provider>
  );
};

export default CreditBalanceList;
